import React, { useState } from 'react';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setIsLoading(true);

    // Add better validation and trimming
    const trimmedEmail = email.trim().toLowerCase();
    
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      setIsLoading(false);
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      return;
    }

    const auth = getAuth();
    
    // Add debugging logs
    console.log('Auth instance:', auth);
    console.log('Attempting to send reset email to:', trimmedEmail);
    
    try {
      // Use the trimmed and lowercase email
      await sendPasswordResetEmail(auth, trimmedEmail);
      console.log('Reset email sent successfully');
      setMessage('Password reset email sent. Check your inbox and spam folder.');
      
      // Clear the email field after successful send
      setEmail('');
      
    } catch (err) {
      console.error('Password reset error:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      
      // Handle specific errors with more detailed messages
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address.');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address.');
          break;
        case 'auth/too-many-requests':
          setError('Too many password reset requests. Please wait a few minutes before trying again.');
          break;
        case 'auth/network-request-failed':
          setError('Network error. Please check your internet connection and try again.');
          break;
        case 'auth/internal-error':
          setError('An internal error occurred. Please try again later.');
          break;
        default:
          setError(`Failed to send reset email: ${err.message || 'Please try again later.'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const styles = {
    // ... (keep all your existing styles as they are)
    forgotPasswordWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url("https://images.pexels.com/photos/258160/pexels-photo-258160.jpeg") no-repeat center center',
      backgroundSize: 'cover',
      padding: '16px',
      boxSizing: 'border-box',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
    },

    forgotPasswordContainer: {
      backgroundColor: '#f9f9f9',
      borderRadius: '10px',
      boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)',
      padding: '28px 28px',
      width: '100%',
      maxWidth: '400px',
      position: 'relative',
      zIndex: 1,
      animation: 'fadeIn 0.4s ease-out',
      margin: '20px 0',
    },

    forgotPasswordBox: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '18px',
    },

    logoContainer: {
      marginTop: '30px',
      marginBottom: '10px',
      textAlign: 'center',
    },

    logo: {
      width: '200px',
      height: 'auto',
      maxWidth: '100%',
      objectFit: 'contain',
    },

    authTitle: {
      fontSize: '2rem',
      fontWeight: '700',
      marginBottom: '0.25rem',
      textAlign: 'center',
      color: '#1a365d',
      letterSpacing: '-1px',
    },

    authSubtitle: {
      color: '#4a5568',
      textAlign: 'center',
      marginBottom: '1.5rem',
      fontSize: '1rem',
      fontWeight: '400',
    },

    forgotPasswordForm: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },

    formGroup: {
      width: '100%',
    },

    formLabel: {
      display: 'block',
      fontSize: '13px',
      color: '#4a5568',
      marginBottom: '6px',
      fontWeight: '500',
    },

    formInput: {
      width: '100%',
      padding: '12px 14px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#2d3748',
      transition: 'all 0.3s ease',
      backgroundColor: '#f8fafc',
      boxSizing: 'border-box',
    },

    formInputFocus: {
      outline: 'none',
      borderColor: '#4a5568',
      boxShadow: '0 0 0 3px rgba(74, 85, 104, 0.1)',
      backgroundColor: '#ffffff',
    },

    submitButton: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#4a5568',
      color: '#fff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '8px',
      marginTop: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    },

    submitButtonHover: {
      backgroundColor: '#2d3748',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15)',
    },

    submitButtonDisabled: {
      backgroundColor: '#cbd5e0',
      cursor: 'not-allowed',
      opacity: '0.8',
    },

    spinner: {
      border: '2px solid rgba(255, 255, 255, 0.3)',
      borderTop: '2px solid #fff',
      borderRadius: '50%',
      width: '16px',
      height: '16px',
      animation: 'spin 0.8s linear infinite',
    },

    errorMessage: {
      backgroundColor: '#fff5f5',
      color: '#c53030',
      border: '1px solid #fed7d7',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '13px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      textAlign: 'left',
      width: '100%',
      boxSizing: 'border-box',
      lineHeight: '1.4',
    },

    successMessage: {
      backgroundColor: '#f0fdf4',
      color: '#166534',
      border: '1px solid #bbf7d0',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '13px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      textAlign: 'left',
      width: '100%',
      boxSizing: 'border-box',
      lineHeight: '1.4',
    },

    backToLogin: {
      textAlign: 'center',
      marginTop: '1rem',
    },

    link: {
      color: '#4a5568',
      textDecoration: 'none',
      fontWeight: '600',
      transition: 'all 0.2s ease',
    },

    linkHover: {
      color: '#2d3748',
      textDecoration: 'underline',
      textUnderlineOffset: '3px',
    },
  };

  return (
    <div style={styles.forgotPasswordWrapper}>
      <div style={styles.forgotPasswordContainer}>
        <div style={styles.forgotPasswordBox}>
          {/* Logo */}
          <div style={styles.logoContainer}>
            <img 
              src="/logo.png" 
              alt="Assetra Asset Management Software" 
              style={styles.logo}
            />
          </div>

          {/* Title and Subtitle */}
          <h2 style={styles.authTitle}>Forgot Password?</h2>
          <p style={styles.authSubtitle}>Enter your email to receive a password reset link</p>
          
          {/* Error Message */}
          {error && (
            <div style={styles.errorMessage}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          {/* Success Message */}
          {message && (
            <div style={styles.successMessage}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {message}
            </div>
          )}
          
          {/* Form */}
          <form onSubmit={handleResetPassword} style={styles.forgotPasswordForm}>
            <div style={styles.formGroup}>
              <label htmlFor="email" style={styles.formLabel}>Email address</label>
              <input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                required
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                style={styles.formInput}
                onFocus={(e) => {
                  Object.assign(e.target.style, styles.formInputFocus);
                }}
                onBlur={(e) => {
                  Object.assign(e.target.style, styles.formInput);
                }}
              />
            </div>
            
            <button 
              type="submit" 
              disabled={isLoading}
              style={{
                ...styles.submitButton,
                ...(isLoading ? styles.submitButtonDisabled : {})
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  Object.assign(e.target.style, styles.submitButtonHover);
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  Object.assign(e.target.style, styles.submitButton);
                }
              }}
            >
              {isLoading ? (
                <>
                  <div style={styles.spinner}></div>
                  Sending...
                </>
              ) : (
                'Send Reset Link'
              )}
            </button>
          </form>

          {/* Back to Login */}
          <div style={styles.backToLogin}>
            <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
              Remember your password?{' '}
              <a 
                href="/login" 
                style={styles.link}
                onMouseEnter={(e) => {
                  Object.assign(e.target.style, styles.linkHover);
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.target.style, styles.link);
                }}
              >
                Back to Login
              </a>
            </p>
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @media (max-width: 480px) {
            .forgotPasswordContainer {
              padding: 24px 20px !important;
              max-width: 100% !important;
            }
            
            .logo {
              width: 180px !important;
            }
          }

          @media (max-width: 360px) {
            .forgotPasswordContainer {
              padding: 20px 16px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default ForgotPassword;
